import { WidgetProps } from '@rjsf/utils';
import { Multiselect, MultiselectProps } from '@amzn/awsui-components-react-v3';
import * as React from 'react';
import { useMemo } from 'react';

interface Option {
  label?: string;
  value?: string;
  options?: Option[];
}

/**
 * Custom multiselect widget that supports grouped options with visual headers.
 *
 * Schema format:
 * {
 *   "type": "array",
 *   "items": {
 *     "type": "string",
 *     "enum": ["[Group Name] Option Label", "Ungrouped Option"]
 *   }
 * }
 *
 * Options with "[GroupName]" prefix will be grouped under that category header.
 * Options without prefix will appear at the top level.
 */
export function GroupedMultiselectWidget(props: WidgetProps) {
  const enumOptions = props.options?.enumOptions || [];
  const selectedValues = Array.isArray(props.value) ? props.value : [];

  // Parse options and create grouped structure
  const groupedOptions = useMemo(() => {
    const groups: Record<string, Option[]> = {};
    const topLevel: Option[] = [];
    let otherOption: Option | null = null;

    enumOptions.forEach((opt) => {
      const value = String(opt.value);
      const match = value.match(/^\[([^\]]+)\]\s*(.+)$/);

      if (match) {
        // Grouped option: "[Group Name] Option Label"
        const groupName = match[1];
        const optionLabel = match[2];

        if (!groups[groupName]) {
          groups[groupName] = [];
        }

        groups[groupName].push({
          label: optionLabel,
          value: value,
        });
      } else {
        // Top-level option
        const option = {
          label: value,
          value: value,
        };

        // Keep "Other" separate to add at the end
        if (value === 'Other') {
          otherOption = option;
        } else {
          topLevel.push(option);
        }
      }
    });

    // Build the options array with group headers
    const result: Option[] = [];

    // Add top-level options first (except "Other")
    if (topLevel.length > 0) {
      result.push(...topLevel);
    }

    // Add grouped options with headers
    Object.entries(groups).forEach(([groupName, groupOpts]) => {
      result.push({
        label: groupName,
        options: groupOpts,
      });
    });

    // Add "Other" at the very end
    if (otherOption) {
      result.push(otherOption);
    }

    return result;
  }, [enumOptions]);

  // Flatten all options to find selected ones
  const allFlatOptions = useMemo(() => {
    const flat: Option[] = [];
    groupedOptions.forEach((opt) => {
      if (opt.options) {
        flat.push(...opt.options);
      } else if (opt.value) {
        flat.push(opt);
      }
    });
    return flat;
  }, [groupedOptions]);

  const selectedOptions = useMemo(() => {
    return allFlatOptions.filter((opt) => selectedValues.includes(opt.value));
  }, [allFlatOptions, selectedValues]);

  const handleChange: MultiselectProps['onChange'] = ({ detail }) => {
    const newValues = detail.selectedOptions.map((opt) => opt.value);
    props.onChange(newValues);
  };

  return (
    <Multiselect
      selectedOptions={selectedOptions}
      onChange={handleChange}
      options={groupedOptions}
      placeholder={props.placeholder || 'Choose all applicable options'}
      filteringType='auto'
      disabled={props.disabled || props.readonly}
    />
  );
}
